import { useState, useEffect, useRef } from 'react'

interface SearchBarProps {
  onSearch?: (query: string) => void
  value?: string
  placeholder?: string
  loading?: boolean
}

export default function SearchBar({ onSearch, value, placeholder, loading }: SearchBarProps) {
  const [query, setQuery] = useState(value || '')
  const lastSubmitted = useRef(value || '')

  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value)
      lastSubmitted.current = value
    }
  }, [value])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch && query !== lastSubmitted.current) {
        lastSubmitted.current = query
        onSearch(query)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (onSearch && query !== lastSubmitted.current) {
      lastSubmitted.current = query
      onSearch(query)
    }
  }

  const handleClear = () => {
    setQuery('')
    if (onSearch && '' !== lastSubmitted.current) {
      lastSubmitted.current = ''
      onSearch('')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex items-center gap-2 bg-[var(--bg-tint)] border border-[var(--border)] rounded-xl p-2 shadow-sm">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-[var(--text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder || "What item do you need?"}
            className="w-full pl-12 pr-10 py-3 bg-white border border-[var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)] focus:border-transparent text-[var(--text-primary)] placeholder-[var(--text-muted)]"
          />
          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary shrink-0"
          aria-label="Search"
        >
          {loading ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>
      </div>
    </form>
  )
}
